import { useEffect, useState, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import {
  Package,
  FileText,
  Plus,
  Search,
  MoreHorizontal,
  Trash2,
  Edit,
  ChevronUp,
  ChevronDown,
  ImageIcon,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { usePlacas } from '../../hooks/usePlacas';
import { PlacaQuotesSection } from './placa-quotes';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../lib/utils';
import type {
  PlacaProduct,
  PlacaActiveTab,
} from '../../types/placas';

interface PlacasPageProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  activeTab: PlacaActiveTab;
  setActiveTab: (tab: PlacaActiveTab) => void;
}

export function PlacasPage({
  accessToken,
  refreshToken,
  attemptRefresh,
  activeTab,
  setActiveTab,
}: PlacasPageProps) {
  const toast = useToast();
  const placas = usePlacas({ accessToken, refreshToken, attemptRefresh, externalActiveTab: activeTab });

  // Local dialog state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PlacaProduct | null>(null);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);

  // Product form state
  const [productName, setProductName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productImageUrl, setProductImageUrl] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productActive, setProductActive] = useState(true);
  const [productMinQuantity, setProductMinQuantity] = useState('1');
  const [productDisplayOrder, setProductDisplayOrder] = useState('0');
  const [productDimensions, setProductDimensions] = useState<Array<{
    id?: string;
    widthCm: string;
    heightCm: string;
    label?: string;
    price: string;
    active?: boolean;
  }>>([]);

  // Image upload state
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch data when tab changes
  useEffect(() => {
    if (accessToken) {
      placas.fetchData();
    }
  }, [activeTab, accessToken]);

  // Format currency as BRL
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Product handlers
  const openProductDialog = (product?: PlacaProduct) => {
    if (product) {
      setSelectedProduct(product);
      setProductName(product.name);
      setProductCode(product.code || '');
      setProductDescription(product.description || '');
      setProductImageUrl(product.imageUrl || '');
      setProductPrice(String(product.price));
      setProductActive(product.active);
      setProductMinQuantity(String(product.minQuantity ?? 1));
      setProductDisplayOrder(String(product.displayOrder));

      // Populate dimensions if they exist
      if (product.dimensions && product.dimensions.length > 0) {
        setProductDimensions(
          product.dimensions.map(dim => ({
            id: dim.id,
            widthCm: String(dim.widthCm),
            heightCm: String(dim.heightCm),
            label: dim.label,
            price: String(dim.price),
            active: dim.active
          }))
        );
      } else {
        setProductDimensions([]);
      }
    } else {
      setSelectedProduct(null);
      setProductName('');
      setProductCode('');
      setProductDescription('');
      setProductImageUrl('');
      setProductPrice('');
      setProductActive(true);
      setProductMinQuantity('1');
      setProductDisplayOrder('0');
      setProductDimensions([]);
    }
    setPendingImageFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setProductDialogOpen(true);
  };

  // Image select - staged locally, only uploaded on save
  const handleImageSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.match(/^image\/(png|jpeg|jpg|webp|gif|avif)$/)) {
      toast.error('Formato de imagem invalido. Use PNG, JPG, WEBP, GIF ou AVIF.');
      return;
    }

    setPendingImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, [toast]);

  // Image remove - pending file just clears state, existing GCS image deletes immediately
  const handleImageRemove = useCallback(async () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setPendingImageFile(null);
    setPreviewUrl(null);

    if (selectedProduct && productImageUrl) {
      setImageUploading(true);
      try {
        await deleteImageFromGcs(selectedProduct.id);
        setProductImageUrl('');
        toast.success('Imagem removida!');
        await placas.fetchProducts();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Erro ao remover imagem');
      } finally {
        setImageUploading(false);
      }
    }
  }, [selectedProduct, productImageUrl, accessToken, toast]);

  // Helper to upload image to GCS for a given product ID
  const uploadImageToGcs = async (productId: string, file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/backoffice/placas/products/${productId}/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: formData,
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error?.message || 'Falha ao enviar imagem');
    }
    const payload = await response.json();
    return payload.imageUrl || payload.data?.imageUrl || null;
  };

  // Helper to delete image from GCS for a given product ID
  const deleteImageFromGcs = async (productId: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/backoffice/placas/products/${productId}/image`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error?.message || 'Falha ao remover imagem');
    }
  };

  const handleSaveProduct = async () => {
    if (!productName.trim()) {
      toast.error('Nome e obrigatorio');
      return;
    }

    // Validation: if dimensions exist, validate each one. If no dimensions, require base price
    if (productDimensions.length > 0) {
      for (let i = 0; i < productDimensions.length; i++) {
        const dim = productDimensions[i];
        if (!dim.widthCm || parseFloat(dim.widthCm) <= 0) {
          toast.error(`Dimensao ${i + 1}: Largura deve ser maior que zero`);
          return;
        }
        if (!dim.heightCm || parseFloat(dim.heightCm) <= 0) {
          toast.error(`Dimensao ${i + 1}: Altura deve ser maior que zero`);
          return;
        }
        if (!dim.price || parseFloat(dim.price) <= 0) {
          toast.error(`Dimensao ${i + 1}: Preco deve ser maior que zero`);
          return;
        }
      }
    } else {
      if (!productPrice || parseFloat(productPrice) <= 0) {
        toast.error('Preco deve ser maior que zero');
        return;
      }
    }

    // Build dimensions array - filter out undefined id fields for new dimensions
    const dimensions = productDimensions.map((dim, index) => {
      const dimension: any = {
        widthCm: parseFloat(dim.widthCm),
        heightCm: parseFloat(dim.heightCm),
        label: dim.label || undefined,
        price: parseFloat(dim.price),
        displayOrder: index,
        active: dim.active ?? true
      };
      if (dim.id) {
        dimension.id = dim.id;
      }
      return dimension;
    });

    const data = {
      name: productName.trim(),
      code: productCode.trim() || undefined,
      description: productDescription.trim() || undefined,
      price: parseFloat(productPrice),
      active: productActive,
      minQuantity: parseInt(productMinQuantity) || 1,
      displayOrder: parseInt(productDisplayOrder) || 0,
      dimensions: dimensions.length > 0 ? dimensions : undefined,
    };

    let result;
    if (selectedProduct) {
      result = await placas.updateProduct(selectedProduct.id, data);
    } else {
      result = await placas.createProduct(data);
    }

    if (!result.success) {
      toast.error(result.error);
      return;
    }

    const productId = selectedProduct?.id || result.data?.id;

    // Upload pending image to GCS (after product is saved)
    let imageUploadFailed = false;
    if (productId && pendingImageFile) {
      setImageUploading(true);
      try {
        await uploadImageToGcs(productId, pendingImageFile);
      } catch (error) {
        imageUploadFailed = true;
        toast.error(
          selectedProduct
            ? (error instanceof Error ? error.message : 'Erro ao enviar imagem')
            : 'Produto criado, mas falha ao enviar imagem. Edite o produto para adicionar.'
        );
      } finally {
        setImageUploading(false);
      }
    }

    if (!imageUploadFailed) {
      toast.success(selectedProduct ? 'Produto atualizado!' : 'Produto criado!');
    }
    setProductDialogOpen(false);
    setPendingImageFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await placas.fetchProducts();
  };

  const handleDeleteProduct = async (id: string) => {
    setProductToDelete(id);
    setConfirmDeleteDialogOpen(true);
  };

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return;

    const result = await placas.deleteProduct(productToDelete);
    if (result.success) {
      toast.success('Produto excluido!');
      setConfirmDeleteDialogOpen(false);
      setProductToDelete(null);
    } else {
      toast.error(result.error);
    }
  };

  const handleReorderProduct = async (productId: string, direction: 'up' | 'down') => {
    if (!accessToken) return;

    const sorted = [...placas.products].sort((a, b) => a.displayOrder - b.displayOrder);
    const currentIndex = sorted.findIndex(p => p.id === productId);

    if (
      currentIndex < 0 ||
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === sorted.length - 1)
    ) {
      return;
    }

    // Move element in array
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const [moved] = sorted.splice(currentIndex, 1);
    sorted.splice(targetIndex, 0, moved);

    // Assign sequential display orders and update all that changed
    const updates: Promise<{ success: boolean }>[] = [];
    sorted.forEach((p, i) => {
      if (p.displayOrder !== i) {
        updates.push(placas.updateProduct(p.id, { displayOrder: i }));
      }
    });

    const results = await Promise.all(updates);
    if (results.some(r => !r.success)) {
      toast.error('Erro ao reordenar produtos');
    }
    await placas.fetchProducts();
  };

  const handleSetProductPosition = async (productId: string, newPosition: number) => {
    if (!accessToken) return;

    const sorted = [...placas.products].sort((a, b) => a.displayOrder - b.displayOrder);
    const currentIndex = sorted.findIndex(p => p.id === productId);
    if (currentIndex < 0) return;

    // Clamp to valid range (1-based input, 0-based internal)
    const targetIndex = Math.max(0, Math.min(sorted.length - 1, newPosition - 1));
    if (targetIndex === currentIndex) return;

    // Move element in array
    const [moved] = sorted.splice(currentIndex, 1);
    sorted.splice(targetIndex, 0, moved);

    // Assign sequential display orders and update all that changed
    const updates: Promise<{ success: boolean }>[] = [];
    sorted.forEach((p, i) => {
      if (p.displayOrder !== i) {
        updates.push(placas.updateProduct(p.id, { displayOrder: i }));
      }
    });

    const results = await Promise.all(updates);
    if (results.some(r => !r.success)) {
      toast.error('Erro ao reordenar produtos');
    }
    await placas.fetchProducts();
  };

  return (
    <>
      {/* Products Tab */}
      {activeTab === 'products' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Produtos de Placas de Premiacao</CardTitle>
                <CardDescription>
                  Gerenciamento de placas de premiacao disponiveis para orcamento
                </CardDescription>
              </div>
              <Button onClick={() => openProductDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Produto
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar produtos..."
                  value={placas.searchTerm}
                  onChange={(e) => placas.setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={placas.includeInactive}
                  onCheckedChange={placas.setIncludeInactive}
                />
                <Label className="text-sm">Incluir inativos</Label>
              </div>
            </div>

            {placas.loading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Posicao</TableHead>
                    <TableHead className="w-[60px]">Imagem</TableHead>
                    <TableHead>Codigo</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Preco</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const sortedProducts = [...placas.products].sort((a, b) => a.displayOrder - b.displayOrder);

                    if (sortedProducts.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            Nenhum produto encontrado
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return sortedProducts.map((product, index) => {
                      const isFirst = index === 0;
                      const isLast = index === sortedProducts.length - 1;

                      return (
                        <TableRow key={product.id}>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min={1}
                                max={sortedProducts.length}
                                defaultValue={index + 1}
                                key={`${product.id}-${index}`}
                                className="h-7 w-12 text-center text-sm px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const val = parseInt((e.target as HTMLInputElement).value);
                                    if (!isNaN(val)) handleSetProductPosition(product.id, val);
                                    (e.target as HTMLInputElement).blur();
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value);
                                  if (!isNaN(val) && val !== index + 1) {
                                    handleSetProductPosition(product.id, val);
                                  }
                                }}
                              />
                              <div className="flex flex-col gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  disabled={isFirst}
                                  onClick={() => handleReorderProduct(product.id, 'up')}
                                >
                                  <ChevronUp className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  disabled={isLast}
                                  onClick={() => handleReorderProduct(product.id, 'down')}
                                >
                                  <ChevronDown className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {product.imageUrl ? (
                              <img
                                src={`${product.imageUrl}?t=${Date.now()}`}
                                alt={product.name}
                                className="h-9 w-9 rounded object-cover"
                              />
                            ) : (
                              <div className="h-9 w-9 rounded bg-muted flex items-center justify-center">
                                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono">{product.code}</TableCell>
                          <TableCell>{product.name}</TableCell>
                          <TableCell className="font-medium">
                            {product.dimensions && product.dimensions.length > 0
                              ? `${product.dimensions.length} tamanho(s)`
                              : formatCurrency(product.price)}
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => placas.toggleProductStatus(product.id)}
                              className="hover:opacity-80 transition-opacity"
                              title="Clique para alternar status"
                            >
                              <Badge variant={product.active ? 'default' : 'secondary'} className="cursor-pointer">
                                {product.active ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </button>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openProductDialog(product)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteProduct(product.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quotes Tab */}
      {activeTab === 'quotes' && (
        <div className="space-y-6">
          <PlacaQuotesSection
            quotes={placas.quotes}
            quotesLoading={placas.loading}
            accessToken={accessToken}
            products={placas.products}
            onRefresh={async () => {
              await placas.fetchQuotes();
            }}
            onUpdateStatus={async (quoteId: string, status: string) => {
              const result = await placas.updateQuoteStatus(quoteId, status);
              if (result.success) {
                toast.success('Status atualizado!');
              } else {
                toast.error(result.error);
              }
            }}
            onUpdateQuoteItem={placas.updateQuoteItem}
            onDeleteQuoteItem={placas.deleteQuoteItem}
            onAddQuoteItem={placas.addQuoteItem}
            onMarkAsViewed={async (quoteId: string) => {
              const result = await placas.markPlacaQuoteAsViewed(quoteId);
              if (!result.success && result.error) {
                toast.error(result.error);
              }
            }}
          />
        </div>
      )}

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do produto de placa de premiacao
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="product-name">
                Nome <span className="text-destructive">*</span>
              </Label>
              <Input
                id="product-name"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Ex: Placa de Premiacao Crystal"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="product-code">Codigo</Label>
              <Input
                id="product-code"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
                placeholder="Ex: PLC-01 (auto se vazio)"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="product-description">Descricao</Label>
              <Textarea
                id="product-description"
                value={productDescription}
                onChange={(e) => setProductDescription(e.target.value)}
                placeholder="Descricao detalhada do produto..."
                rows={3}
              />
            </div>

            {/* Image Upload Section - unified for create and edit */}
            <div className="grid gap-2">
              <Label>Imagem do Produto</Label>
              {pendingImageFile && previewUrl ? (
                /* New image selected locally (not yet uploaded) */
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  <div className="p-3 flex justify-between items-center">
                    <span className="text-sm text-muted-foreground truncate flex-1 mr-2">
                      {pendingImageFile.name}
                    </span>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleImageRemove}
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              ) : productImageUrl ? (
                /* Existing GCS image (edit mode) */
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <div className="relative bg-gradient-to-b from-[#151515] to-[#0d0d0d]">
                    <img
                      key={productImageUrl}
                      src={`${productImageUrl}?t=${Date.now()}`}
                      alt={productName || 'Produto'}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="p-3 flex justify-between items-center bg-card">
                    <div className="flex-1 mr-2 overflow-hidden">
                      <div className="text-sm text-muted-foreground truncate">
                        {productImageUrl.split('/').pop()}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleImageRemove}
                    >
                      Remover imagem
                    </Button>
                  </div>
                </div>
              ) : (
                /* No image - show picker */
                <div className="rounded-lg border border-dashed border-muted-foreground/25 bg-muted/20 p-6 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Package className="h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground mb-2">
                      Escolha uma imagem (opcional)
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Escolher imagem
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/avif"
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, WEBP, GIF ou AVIF
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="product-price">
                  Preco (R$) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="product-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={productPrice}
                  onChange={(e) => setProductPrice(e.target.value)}
                  placeholder="Ex: 150.00"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="product-min-quantity">Qtd Minima</Label>
                <Input
                  id="product-min-quantity"
                  type="number"
                  min="1"
                  value={productMinQuantity}
                  onChange={(e) => setProductMinQuantity(e.target.value)}
                  placeholder="1"
                />
              </div>
            </div>

            {/* Dimensions Section */}
            <div className="grid gap-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Dimensoes (opcional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setProductDimensions([
                      ...productDimensions,
                      { widthCm: '', heightCm: '', price: '', active: true }
                    ]);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar Dimensao
                </Button>
              </div>

              {productDimensions.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Quando dimensoes sao definidas, cada uma tem seu proprio preco.
                </p>
              )}

              {productDimensions.length > 0 && (
                <div className="space-y-2">
                  {productDimensions.map((dim, index) => (
                    <div key={index} className="flex items-center gap-2 border rounded-lg p-3">
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={dim.widthCm}
                          onChange={(e) => {
                            const newDimensions = [...productDimensions];
                            newDimensions[index].widthCm = e.target.value;
                            setProductDimensions(newDimensions);
                          }}
                          placeholder="L"
                          className="w-20"
                        />
                        <span className="text-muted-foreground">x</span>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={dim.heightCm}
                          onChange={(e) => {
                            const newDimensions = [...productDimensions];
                            newDimensions[index].heightCm = e.target.value;
                            setProductDimensions(newDimensions);
                          }}
                          placeholder="A"
                          className="w-20"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <Label className="text-sm text-muted-foreground">R$</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={dim.price}
                          onChange={(e) => {
                            const newDimensions = [...productDimensions];
                            newDimensions[index].price = e.target.value;
                            setProductDimensions(newDimensions);
                          }}
                          placeholder="0.00"
                          className="w-28"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setProductDimensions(productDimensions.filter((_, i) => i !== index));
                        }}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="product-active"
                checked={productActive}
                onCheckedChange={setProductActive}
              />
              <Label htmlFor="product-active" className="cursor-pointer">
                Produto ativo
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveProduct} disabled={placas.saving}>
              {placas.saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteDialogOpen} onOpenChange={setConfirmDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusao</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este produto? Esta acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDeleteDialogOpen(false);
                setProductToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDeleteProduct}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
